/**
 * CR-6444: dependent-filter cascade logic for the NCR filter panel.
 *
 * Implements the same cascade behaviour as usePOLinesFilterCascades:
 * - Division → Site (selecting division filters available sites)
 * - Site → Warehouse (selecting site filters available warehouses)
 * - Site → Supplier Site (selecting a site keeps only the suppliers that
 *   serve it — the ticket's "Selecting a Site should update available
 *   supplier options", missing from the first pass)
 * - Supplier Country → Supplier Site (selecting country filters suppliers)
 * - Supplier Group → Supplier Site (selecting group filters suppliers)
 * - Commodity Group → Commodity Type (selecting group filters types)
 *
 * When a parent filter changes, dependent children are cleared if they
 * become invalid (e.g., selected site is not in the new division).
 *
 * Site → Supplier Site reads `supplierCustomers`: supplier company id → the
 * customer ids it has an active relationship with (the `customers` field of
 * /core/companies/for-filter/, the same source the ASN and Pricing panels
 * use). When that map is empty — the call failed, or the supplier portal,
 * where the panel's suppliers are the user's own sites — the Site pick leaves
 * the supplier list alone rather than emptying it.
 */
import { useMemo } from 'react';
import type { NCRFiltersState } from './ncrFilterParams';
import type { Site, Warehouse, SupplierProfile, CompanyGroup } from '../../lib/api';

interface CommodityOption {
  id: number;
  name: string;
  commodity_group?: number | null;
}

interface Division {
  id: number;
  name: string;
}

export interface NCRFilterCascadeOptions {
  divisions: Division[];
  sites: Site[];
  warehouses: Warehouse[];
  suppliers: SupplierProfile[];
  supplierGroups: CompanyGroup[];
  commodityGroups: CommodityOption[];
  commodityTypes: CommodityOption[];
  /** Supplier company id → customer (site) ids it serves. Optional; see file header. */
  supplierCustomers?: Record<number, number[]>;
}

/** Supplier's company id — the value the Supplier Site dropdown carries. */
function getSupplierId(s: SupplierProfile): number | null {
  return s.company_id ?? s.company ?? null;
}

/** True when this supplier is known to serve `siteId` (see file header for the empty-map rule). */
function supplierServesSite(
  s: SupplierProfile,
  siteId: number,
  supplierCustomers: Record<number, number[]>,
): boolean {
  const id = getSupplierId(s);
  if (id == null) return false;
  return (supplierCustomers[id] ?? []).includes(siteId);
}

/** Extract supplier's group ID from company_details. */
function getSupplierGroupId(s: SupplierProfile): number | null {
  const details = s.company_details as unknown as { group?: number | null } | undefined;
  return details?.group ?? null;
}

/** Extract supplier's country from various possible locations. */
function getSupplierCountry(s: SupplierProfile): string {
  const direct = (s as unknown as { country?: string | null }).country;
  const nested = (s.company_details as unknown as { country?: string | null } | undefined)?.country;
  return direct || nested || '';
}

export function useNCRFilterCascades(
  values: NCRFiltersState,
  options: NCRFilterCascadeOptions,
  onChange: (next: Partial<NCRFiltersState>) => void,
) {
  const { sites, warehouses, suppliers, commodityTypes } = options;
  const supplierCustomers = options.supplierCustomers ?? {};
  // No link data at all → the Site pick must not empty the supplier list.
  const hasSupplierLinks = Object.keys(supplierCustomers).length > 0;

  // --- Filtered option lists based on parent selections ---

  // Distinct company countries for the Company Country dropdown (derived from sites)
  const companyCountryOptions = useMemo(() => {
    const set = new Set<string>();
    sites.forEach((s) => {
      const country = (s as unknown as { country?: string | null }).country;
      if (country) set.add(country);
    });
    return Array.from(set).sort().map((c) => ({ value: c, label: c }));
  }, [sites]);

  // Sites: narrowed by selected Division and Company Country
  const filteredSites = useMemo(() => {
    let list = sites;
    // Filter by Division (group_id)
    if (values.divisionFilter) {
      const divId = Number(values.divisionFilter);
      list = list.filter((s) => {
        const siteGroupId = (s as unknown as { group_id?: number | null }).group_id;
        return siteGroupId === divId;
      });
    }
    // Filter by Company Country
    if (values.companyCountryFilter) {
      list = list.filter((s) => {
        const country = (s as unknown as { country?: string | null }).country;
        return country === values.companyCountryFilter;
      });
    }
    return list;
  }, [sites, values.divisionFilter, values.companyCountryFilter]);

  // Warehouses: narrowed by selected Site
  const filteredWarehouses = useMemo(() => {
    if (!values.siteFilter) return warehouses;
    const siteId = Number(values.siteFilter);
    return warehouses.filter((w) => w.site === siteId);
  }, [warehouses, values.siteFilter]);

  // Suppliers: narrowed by Site, Supplier Country and Supplier Group
  const filteredSuppliers = useMemo(() => {
    let list = suppliers;
    if (values.siteFilter && hasSupplierLinks) {
      const siteId = Number(values.siteFilter);
      list = list.filter((s) => supplierServesSite(s, siteId, supplierCustomers));
    }
    if (values.supplierGroupFilter) {
      const gid = Number(values.supplierGroupFilter);
      list = list.filter((s) => getSupplierGroupId(s) === gid);
    }
    if (values.supplierCountryFilter) {
      list = list.filter((s) => getSupplierCountry(s) === values.supplierCountryFilter);
    }
    return list;
  }, [suppliers, supplierCustomers, hasSupplierLinks, values.siteFilter, values.supplierGroupFilter, values.supplierCountryFilter]);

  // Distinct supplier countries for the Country dropdown
  const supplierCountryOptions = useMemo(() => {
    const set = new Set<string>();
    suppliers.forEach((s) => {
      const c = getSupplierCountry(s);
      if (c) set.add(c);
    });
    return Array.from(set).sort().map((c) => ({ value: c, label: c }));
  }, [suppliers]);

  // Commodity Types: narrowed by selected Commodity Group
  const filteredCommodityTypes = useMemo(() => {
    if (!values.commodityGroupFilter) return commodityTypes;
    const gid = Number(values.commodityGroupFilter);
    return commodityTypes.filter((t) => t.commodity_group === gid);
  }, [commodityTypes, values.commodityGroupFilter]);

  // --- Cascade change handlers (clear now-invalid dependents) ---

  const handleDivisionChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { divisionFilter: value };
    // If division changes and site is selected, check if site is still valid
    if (value && values.siteFilter) {
      const siteId = Number(values.siteFilter);
      const site = sites.find((s) => s.id === siteId);
      const siteGroupId = (site as unknown as { group_id?: number | null })?.group_id;
      if (site && String(siteGroupId ?? '') !== value) {
        updates.siteFilter = '';
        updates.warehouseFilter = '';
      }
    }
    onChange(updates);
  };

  const handleCompanyCountryChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { companyCountryFilter: value };
    // If country changes and site is selected, check if site is still valid
    if (value && values.siteFilter) {
      const siteId = Number(values.siteFilter);
      const site = sites.find((s) => s.id === siteId);
      const siteCountry = (site as unknown as { country?: string | null })?.country;
      if (site && siteCountry !== value) {
        updates.siteFilter = '';
        updates.warehouseFilter = '';
      }
    }
    onChange(updates);
  };

  const handleSiteChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { siteFilter: value };
    // If site changes, check if warehouse is still valid
    if (values.warehouseFilter) {
      const wh = warehouses.find((w) => String(w.id) === values.warehouseFilter);
      if (!value || !wh || String(wh.site) !== value) {
        updates.warehouseFilter = '';
      }
    }
    // If site changes and a supplier is selected, drop it unless it serves the new site
    if (value && values.supplierFilter && hasSupplierLinks) {
      const supplierId = Number(values.supplierFilter);
      const supplier = suppliers.find((s) => getSupplierId(s) === supplierId);
      if (supplier && !supplierServesSite(supplier, Number(value), supplierCustomers)) {
        updates.supplierFilter = '';
      }
    }
    onChange(updates);
  };

  const handleSupplierGroupChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { supplierGroupFilter: value };
    // If group changes and supplier is selected, check if supplier is still valid
    if (value && values.supplierFilter) {
      const supplierId = Number(values.supplierFilter);
      const supplier = suppliers.find((s) => getSupplierId(s) === supplierId);
      if (supplier && String(getSupplierGroupId(supplier) ?? '') !== value) {
        updates.supplierFilter = '';
      }
    }
    onChange(updates);
  };

  const handleSupplierCountryChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { supplierCountryFilter: value };
    // If country changes and supplier is selected, check if supplier is still valid
    if (value && values.supplierFilter) {
      const supplierId = Number(values.supplierFilter);
      const supplier = suppliers.find((s) => getSupplierId(s) === supplierId);
      if (supplier && getSupplierCountry(supplier) !== value) {
        updates.supplierFilter = '';
      }
    }
    onChange(updates);
  };

  const handleCommodityGroupChange = (value: string) => {
    const updates: Partial<NCRFiltersState> = { commodityGroupFilter: value };
    // Clear commodity type when group changes
    if (value !== values.commodityGroupFilter) {
      updates.commodityTypeFilter = '';
    }
    onChange(updates);
  };

  return {
    // Filtered option lists
    filteredSites,
    filteredWarehouses,
    filteredSuppliers,
    filteredCommodityTypes,
    // Derived option lists
    companyCountryOptions,
    supplierCountryOptions,
    // Cascade handlers
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
    handleCommodityGroupChange,
  };
}
